import React from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "hooks"
import FormElement from "components/Form/FormElement"
import { jsonHash } from "utils/hash"


export const AdditionalInformationForm = ({
    partner,
    setUpdatedPartner,
    errors,
    setErrors,
    isEdit,
    isViewMode,
    editCallback,
    refresh,
    showLocalErrors,
    setShowLocalErrors,
    isAdminEditing
}) => {
    const [formFields, setFormFields] = React.useState({})
    const defaultFormEvents = React.useMemo(() => ({
        ready: true,
        // when form is in edition mode, by default the editable is set at false
        // when the form is not in edition mode, it means creation mode, the form should be editable
        editable: !isEdit,
        viewMode: isViewMode || false
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }), [isEdit])
    const [formEvents, setFormEvents] = React.useState(defaultFormEvents)
    const formHasChanged = jsonHash(partner) !== jsonHash(formFields)

    const { entity, fetchEntity } = useAuth()

    React.useEffect(() => {
        // When partner reloaded (after await fetchData()), set data in formFields
        // setFormFields(partner)
        if(partner) setFormFields(partner)
    },
        [partner])

    React.useEffect(() => {
        // When we create a new partner, we have to pass data to parent directly.
        // We don't have any submit button on this component in this case
        if(!isEdit) setUpdatedPartner((prevFormFields) => ({
            ...prevFormFields,
            site_web: formFields.site_web
        }))
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, formFields])

    const { t } = useTranslation()

    const toggleEditableForm = () => {
        setFormEvents({
            ...formEvents,
            editable: !formEvents.editable
        })
    }

    const discardChanges = async () => {
        toggleEditableForm()

        if (formHasChanged) {
            if (isAdminEditing) {
                await refresh()
            } else {
                await fetchEntity(entity.entity_id)
            }
        }
    }

    const handleInputChange = React.useCallback(
        (event) => {
            const target = event.target
            const value = target.type === "checkbox" ? target.checked : target.value
            const name = target.name
            setFormFields(s => ({
                ...s,
                [name]: value
            }))
        }, []
    )

    const handleValidation = React.useCallback((name, errs) => {
        setErrors((s) => {
            const cleanErrors = [...s.filter((e) => e.origin !== name)]
            return [
                ...cleanErrors,
                ...errs
            ]
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])


    const handleSubmit = async (event) => {
        event.preventDefault()
        // Validate inputs
        const formReady = !(errors.length > 0)

        if (!formReady) {
            setShowLocalErrors(true)
        }
        else {
            // hide error
            setShowLocalErrors(false)
            // prevent form to be submitted again
            setFormEvents(s => ({
                ...s,
                ready: false
            }))

            const updatedPartner = {
                ...formFields,
                site_web: formFields.site_web
            }

            await editCallback(updatedPartner)

            setFormEvents(s => ({
                ...s,
                ready: true,
                editable: false
            }))
        }
    }

    return (
        <form className="c-card p-5 md:p-12 mb-7">
            <h2 className="c-h2 font-semibold mb-5 uppercase">{t("store.additional_infos", { ns: "partner" })}</h2>
            <div>
                <div className="lg:w-1/2 w-full py-5">
                    {/* website */}
                    <FormElement
                        value={formFields.site_web}
                        name="site_web"
                        type="text"
                        label={t("store.website", { ns: "partner" })}
                        required={false}
                        readonly={!formEvents.editable}
                        showErrors={showLocalErrors}
                        onValidate={handleValidation}
                        onChange={handleInputChange}
                    />
                </div>
            </div>

            {isEdit && !isViewMode &&
                <div className="flex justify-end pt-12">
                    {formEvents.editable
                        ?
                        <>
                            <button
                                type="button"
                                className="c-btn c-btn--white-bordered c-btn--m px-12 mx-5"
                                onClick={discardChanges}
                            >
                                {t("global.cancel")}
                            </button>
                            <button
                                type="submit"
                                className="c-btn c-btn--primary c-btn--m px-12 mx-5"
                                onClick={handleSubmit}
                                disabled={!formEvents.ready || !formHasChanged}
                            >
                                {t("global.save")}
                            </button>
                        </>
                        :
                        <button
                            type="button"
                            className="c-btn c-btn--primary px-12 mx-5"
                            onClick={toggleEditableForm}
                        >
                            {t("global.update")}
                        </button>
                    }
                </div>
            }
        </form>

    )
}
